import { action, makeAutoObservable, runInAction } from 'mobx';
import ByteReaderBrowser from '../lib/ByteReaderBrowser';
import { SplitMbox } from '../gflib/src/lib/mbox/SplitMbox';
import { Writable, pipeline } from 'stream';
import { GmailHeaders, decodeStringIconvLite, findNthLine, myUnleak, parseGmailHeaders } from '../lib/utils';
import { doKeyInc } from '../gflib/src/lib/utils/map';
import { ParsedDecodedHeaders } from '../gflib/src/lib/mime-headers/parseAndDecodeHeaders.types';
import { MimeNode } from '../gflib/src/lib/mime-tree/types';
import { ReadableWebToNodeStream } from 'readable-web-to-node-stream';
import { Readable } from 'readable-stream';

type View = 'landing' | 'messageList' | 'other';
export class AppState {
	currentView!: View;
    currentMessage!: OpenedMessage;
    loadedMore!: boolean;
    showBanner!: boolean;
    sorting!: MessageSorting;
    messageQueryAndIxList!: MessageSearchQueryAndMessageIxList;
	mboxList!: MboxData[];
    loadingListElements!: LoadingListElement[];
	isDragging!: boolean;
    cursorXPos!: number;
    isLoadingFilter!: boolean;
    // debug!: number;
    cancellationFunctions!: Array<(()=>void)|undefined>;
    // This field is a stack of indices for the previous array
    // indicating which indixes we have emptied and thus should use again.
    // We take this approach instead of simply pop()ing elements from cancellationFunctions
    // because we would like to guarantee indices remain stable despite asynchronous interference.
    cancellationOpenIndexes!: number[];

	constructor() {
		makeAutoObservable(this);

        this.constructorInit();
	}

    constructorInit = action( () => {
        this.currentView = 'landing';
        this.mboxList = [];
        this.loadingListElements = [];
        this.isDragging = false;
        this.messageQueryAndIxList = {fileIx: 0, label: '', messageIxList: [], grep: '', filteredIxList: []};
        this.loadedMore = false;
        this.showBanner = true;
        this.sorting = {column: 'none', direction: 'asc', page: 0};
        this.currentMessage = {
            mime: '',
            plaintext: '',
            html: '',
            renderHtml: '',
            subject: '',
            subview: 'plaintext',
            attachments: {
                nodeList: [],
                cidMap: new Map<string, number>()
            }
        };
        this.cancellationFunctions = [];
        this.cancellationOpenIndexes = [];
    });

	updateCurrentView = action((newView: View) => {
		this.currentView = newView;
	});
} // interface AppState

export type Subview = 'plaintext'|'html'|'headers'|'mime'|'rawHtml'|'attach';

export interface Openable {
    isOpen: boolean;
}

export interface MboxData extends Openable {
    key: string;
	name: string;
	reader: ByteReaderBrowser;
    isOpen: boolean; // Strictly for constructing folder view
	messageData: MessageData[];
    labelMetadata?: LabelData;
}

export interface LoadingListElement {
    key: string;
    name: string;
    loadedEmailCount: number;
    cancelReadIx: number;
    cancelSplitIx: number;
    cancelOutputIx: number;
}

interface MessageDataWithMessage {
	start: number;
	end: number;
	message: string;
    fromLine?: string;
    // First twenty characters of the message, only to be included in the absence of headers
    messageFirstTwenty?: string;
    headers?: GmailHeaders;
}
type MessageData = Omit<MessageDataWithMessage, 'message'>;

interface LabelData {
    labelCounts: Map<string, number>; // Tells how many of a given label exist
    labelCapitalization: Map<string, string>; // Maps lowercase strings to correctly capitalized version for display
    labelIndices: Map<string, number[]>; // Maps label names to indices of messages bearing that label
    labelTree: LabelTree; // Tree-form of label structure for ease of constructing folder view.
        // Emails without labels go "in" labelTree.labels directly.
}

export interface LabelTree extends Openable {
    label: string;
    children: LabelTree[];
    isOpen: boolean;
}

interface MessageSearchQueryAndMessageIxList {
    fileIx: number;
    label: string;
    grep: string;
    messageIxList: number[];
    filteredIxList: number[];
}

export interface OpenedMessage {
    mime: string;
    plaintext: string;
    html: string;
    renderHtml: string;
    subject: string;
    headers?: ParsedDecodedHeaders;
    subview: Subview;
    attachments: MessageAttachments;
}
export interface MessageAttachments {
    nodeList: MimeNode[];
    cidMap: Map<string, number>;
}

export interface MessageSorting {
    column: EmailTableCol;
    direction: 'asc'|'desc';
    page: number;
}
export type EmailTableCol = 'subject'|'from'|'to'|'date'|'size'|'none';

export const loadNewMbox = action(async function (state: AppState, file: File): Promise<MboxData|undefined> {
	const res: MboxData = {
		name: file.name,
        key: file.name + Math.random(),
		reader: new ByteReaderBrowser(file),
        isOpen: false,
        messageData: [] // This gets overwritten
	};

	// Compute and append message offsets
    const readableStream: Readable = new ReadableWebToNodeStream(file.stream());
	const mboxSplittingStream = new SplitMbox(true);
	const messageData: MessageData[] = [];
    const labelInfo: LabelData = {
        labelCounts: new Map<string, number>(),
        labelCapitalization: new Map<string, string>(),
        labelIndices: new Map<string, number[]>(),
        labelTree: {label: '', children: [], isOpen: false}
    };

    const readIx = popFirstOpenIx(state.cancellationFunctions, state.cancellationOpenIndexes);
    state.cancellationFunctions[readIx] = () => {
        readableStream.destroy();
        runInAction(() => {
            state.cancellationFunctions[readIx] = undefined;
            state.cancellationOpenIndexes.push(readIx);
        });
    };

    const splitIx = popFirstOpenIx(state.cancellationFunctions, state.cancellationOpenIndexes);
    state.cancellationFunctions[splitIx] = () => {
        mboxSplittingStream.destroy();
        runInAction(() => {
            state.cancellationFunctions[splitIx] = undefined;
            state.cancellationOpenIndexes.push(splitIx);
        });
    };

    const outputIx = popFirstOpenIx(state.cancellationFunctions, state.cancellationOpenIndexes);
    state.cancellationFunctions[outputIx] = () => {
        streamToOutput.destroy();
        runInAction(() => {
            state.cancellationFunctions[outputIx] = undefined;
            state.cancellationOpenIndexes.push(outputIx);
        });
    };

    const loadingInfo: LoadingListElement = {
        name: file.name,
        key: res.key,
        loadedEmailCount: 0,
        cancelReadIx: readIx,
        cancelSplitIx: splitIx,
        cancelOutputIx: outputIx
    };
    let loadingIx = state.loadingListElements.push(loadingInfo) - 1;


	const streamToOutput = new Writable({
        objectMode: true,
		write(chunk: MessageDataWithMessage, encoding, callback) {
            try {
                const { start, end, message } = chunk;
                const mData: MessageData = { start, end };
    
                try {
                    mData.headers = parseGmailHeaders(decodeStringIconvLite, message);
                    if(mData.headers?.labels) {
                        // We have the labels from parseGmailHeaders() but still need to generate useful
                        // label-related metadata for the whole mbox and store it for later
                        mData.headers?.labels?.forEach( (label) => {
                            doKeyInc(labelInfo.labelCounts, label.toLowerCase());
                            const labelParts = label.split('/');
                            const lastPart = labelParts[labelParts.length-1];
                            if(!labelInfo.labelCapitalization.has(lastPart.toLowerCase())) {
                                labelInfo.labelCapitalization.set(lastPart.toLowerCase(), lastPart);
                            }
                            if(!labelInfo.labelIndices.has(label.toLowerCase())) {
                                labelInfo.labelIndices.set(label.toLowerCase(), [messageData.length]);
                            } else {
                                // already exists
                                labelInfo.labelIndices.get(label.toLowerCase())?.push(messageData.length);
                            }
                        });
                    }
                
                } catch (e) {
                    console.log(e);
                    mData.headers = {
                        warning: true,
                    };
                    mData.messageFirstTwenty = myUnleak(message.substring(0, 20));
                }
                messageData.push(mData);
                runInAction(() => {
                    // Counting backwards to realign in case someone indexed below us spliced and offset us previously
                    while(!state.loadingListElements[loadingIx] || state.loadingListElements[loadingIx].key !== res.key) {
                        loadingIx--;
                    }
                    state.loadingListElements[loadingIx].loadedEmailCount = messageData.length;
                });
                // state.debug = messageData.length;
                // console.log("mdata len - " + messageData.length);
            } catch(e) {
                console.log(e);
            }
			

			callback();
		},
	});
    
	// readableStream.pipe(mboxSplittingStream).pipe(streamToOutput);
    // async function processStream(): Promise<void> {
    //     return new Promise<void>((resolve, reject) => {
    //         pipeline(readableStream, mboxSplittingStream, streamToOutput);
    //         streamToOutput.on('finish', resolve);
    //         streamToOutput.on('error', reject);
    //     });
    // }

    function processStream(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            pipeline(readableStream, mboxSplittingStream, streamToOutput, (error) => {
                if (error) {
                    reject(error); // Reject the promise if an error occurs
                } else {
                    resolve(); // Resolve the promise if the pipeline completes successfully
                }
            });
        });
    }

    try {
        await processStream();
    } catch(error) {
        console.log(error);
        return;
    }

    runInAction(() => {
        if(state.cancellationFunctions[readIx] !== undefined) {
            state.cancellationFunctions[readIx]!();
        }
        if(state.cancellationFunctions[splitIx] !== undefined) {
            state.cancellationFunctions[splitIx]!();
        }
        if(state.cancellationFunctions[outputIx] !== undefined) {
            state.cancellationFunctions[outputIx]!();
        }
        // Counting backwards to realign in case someone indexed below us spliced and offset us previously
        while(!state.loadingListElements[loadingIx] || state.loadingListElements[loadingIx].key !== res.key) {
            loadingIx--;
        }
        state.loadingListElements.splice(loadingIx, 1);
    });

    if(labelInfo.labelCounts.size > 0) {
        for(const [label] of labelInfo.labelCounts) {
            const labelParts = label.split('/');
            if(labelParts.length === 1) {
                const temp = labelInfo.labelTree.children?.filter((e, index) => {
                    return (e.label === label);
                })[0];
                if(!temp) {
                    // This check prevents parents from being reinserted after a derivative child has been put onto the tree
                    labelInfo.labelTree.children!.push({label: label, children: [], isOpen: false});
                }
            } else {
                let curr = labelInfo.labelTree;
                for(const part of labelParts) {
                    const temp = curr.children?.filter((e, index) => {
                        return (e.label === part);
                    })[0];
                    if(temp) {
                        curr = temp;
                    } else {
                        curr.children!.push({label: part, children: [], isOpen: false});
                        curr = curr.children![curr.children!.length - 1];
                    }
                } // For each label part
            }
        } // For each label

        res.labelMetadata = labelInfo;
    }
	res.messageData = messageData;

    // If the whole file is not a gmail mbox and therefore has no headers
    if(!res.messageData[0].headers) {
        let prevEnd = 0;
        for(const message of res.messageData) {
            message.fromLine = myUnleak((await res.reader.readBytes(prevEnd, message.start)).toString('binary'));
            prevEnd = message.end;
        }
    }

	return res;
}); // loadNewMbox()

function bufferContainsStringIgnoreCase(buffer: Buffer, searchString: string): boolean {
    for (let i = 0; i < buffer.length - searchString.length + 1; i++) {
        let found = true;
        for (let j = 0; j < searchString.length; j++) {
            if (
                buffer[i + j] !== searchString.toLowerCase().charCodeAt(j) &&
                buffer[i + j] !== searchString.toUpperCase().charCodeAt(j)
            ) {
                found = false;
                break;
            }
        }
        if (found) {
            return true;
        }
    }
    return false;
} // bufferContainsString()

// Function that generates filteredIxList from the current sortedIxList
export const filterIxList = action(async (state: AppState) => {
    const cancellationFlag = {flag: false};
    const cancellationIx = popFirstOpenIx(state.cancellationFunctions, state.cancellationOpenIndexes);
    state.cancellationFunctions[cancellationIx] = () => {
        cancellationFlag.flag = true;
        runInAction(() => {
            state.cancellationFunctions[cancellationIx] = undefined;
            state.cancellationOpenIndexes.push(cancellationIx);
        });
    };

    if(state.messageQueryAndIxList.grep.length < 1) {
        state.messageQueryAndIxList.filteredIxList = [...state.messageQueryAndIxList.messageIxList];
    }

    const filterList: Array<number|boolean> = [...state.messageQueryAndIxList.messageIxList];
    const grep = state.messageQueryAndIxList.grep;
    const fIx = state.messageQueryAndIxList.fileIx;
    for(let i = 0; i < filterList.length; i++) {
        if(cancellationFlag.flag) {
            return;
        }
        const reader = state.mboxList[fIx].reader;
        const {start, end}  = state.mboxList[fIx].messageData[filterList[i] as number];
        const message = await reader.readBytes(start, end);

        filterList[i] = bufferContainsStringIgnoreCase(message, grep);
    }

    runInAction( () => {
        state.messageQueryAndIxList.filteredIxList = state.messageQueryAndIxList.messageIxList.filter((val, ix) => {
            return filterList[ix];
        });
        state.sorting = {column: 'none', direction: 'asc', page: 0}; // Reset the column sorting
    });

    
    if(state.cancellationFunctions[cancellationIx]) {
        state.cancellationFunctions[cancellationIx]!();
    }
}); // filterIxList()

// Helper function that actually does the sorting once all the parameters are arranged
const _sortIxList = action((state: AppState) => {
    state.messageQueryAndIxList.filteredIxList.sort((a: number, b: number) => {
        if(state.sorting.column === 'none') {
            return a - b;
        }

        let _a: number, _b: number;
        if(state.sorting.direction === 'asc') {
            _a = a;
            _b = b;
        } else {
            _a = b;
            _b = a;
        }

        const fileIx = state.messageQueryAndIxList.fileIx;
        const aHeaders = state.mboxList[fileIx].messageData[_a].headers;
        const bHeaders = state.mboxList[fileIx].messageData[_b].headers;

        if(!aHeaders) {
            return -1;
        } else if (!bHeaders) {
            return 1;
        }
        
        if(state.sorting.column === 'subject') {
            if(!!aHeaders.subject && !!bHeaders.subject) {
                return aHeaders.subject.localeCompare(bHeaders.subject);
            } else {
                return 0;
            }
        } else if(state.sorting.column === 'from') {
            if(!!aHeaders.from && !!bHeaders.from) {
                return aHeaders.from.localeCompare(bHeaders.from);
            } else {
                return 0;
            }
        } else if(state.sorting.column === 'to') {
            if(!!aHeaders.to && !!bHeaders.to) {
                return aHeaders.to.localeCompare(bHeaders.to);
            } else {
                return 0;
            }
        } else if(state.sorting.column === 'date') {
            if(aHeaders.date !== undefined && bHeaders.date !== undefined) {
                return aHeaders.date - bHeaders.date;
            } else {
                return 0;
            }
        } else if(state.sorting.column === 'size') {
            const aSize = state.mboxList[fileIx].messageData[_a].end - state.mboxList[fileIx].messageData[_a].start;
            const bSize = state.mboxList[fileIx].messageData[_b].end - state.mboxList[fileIx].messageData[_b].start;
            return aSize - bSize;
        };

        return 0;
    });
}); // _sortIxList()

export const updateSorting = action(function (state: AppState, col: EmailTableCol): void {
    let res: MessageSorting;
    if(col === state.sorting.column) {
        if(state.sorting.direction === 'asc') {
            res = {column: col, direction: 'desc', page: 0};
        } else {
            res = {column: 'none', direction: 'asc', page: 0};
        }
    } else {
        res = {column: col, direction: 'asc', page: 0};
    }
    state.sorting = res;

    _sortIxList(state);
}); // updateSorting()

function popFirstOpenIx(ar: Array<any>, openIxAr: Array<number>) {
    if(openIxAr.length > 0) {
        return openIxAr.pop()!;
    }
    return ar.length;
} // findFirstOpenIndex()